/** What-if discounts on whole lines or slices (indicative, not AWS quotes). */
export type SavingsLevers = {
  /**
   * Models cheaper GB·second (Spot / Fargate Spot / Batch on Spot style).
   * Lambda has no Spot SKU — we discount only the GB·s slice inside the Lambda line.
   */
  spotOrPreemptibleCompute: boolean;
  /** Committed capacity / provisioned RCU-WCU style discount on Dynamo line. */
  dynamoCommittedCapacity: boolean;
  /** Cheaper model mix, batch API, or heavier caching on the LLM line. */
  llmEconomyTier: boolean;
  /** Batch / async LLM API vs realtime (індикативна знижка на рядок LLM). */
  llmBatchApi: boolean;
  /**
   * Dev/staging style: EC2/EKS compute and RDS instance hours scaled ~5/7 (off Sat–Sun).
   * Not applied to Lambda, storage, or fixed services (Route53, GuardDuty, etc.).
   */
  scheduleOffWeekends: boolean;
};

export const DEFAULT_SAVINGS_LEVERS: SavingsLevers = {
  spotOrPreemptibleCompute: false,
  dynamoCommittedCapacity: false,
  llmEconomyTier: false,
  llmBatchApi: false,
  scheduleOffWeekends: false,
};

/** Pay this fraction of on-demand GB·s when Spot-like lever is on. */
const SPOT_COMPUTE_FRACTION = 0.42;

/** Multiplier on whole Dynamo line when committed lever is on. */
const DYNAMO_COMMITTED_FACTOR = 0.78;

/** Multiplier on whole LLM line when economy tier lever is on. */
const LLM_ECONOMY_FACTOR = 0.72;

/** Додатковий множник для batch/offline LLM API (після economy tier). */
const LLM_BATCH_API_FACTOR = 0.68;

export function normalizeSavingsLevers(x: unknown): SavingsLevers {
  if (!x || typeof x !== "object") return { ...DEFAULT_SAVINGS_LEVERS };
  const o = x as Record<string, unknown>;
  return {
    spotOrPreemptibleCompute: Boolean(o.spotOrPreemptibleCompute),
    dynamoCommittedCapacity: Boolean(o.dynamoCommittedCapacity),
    llmEconomyTier: Boolean(o.llmEconomyTier),
    llmBatchApi: Boolean(o.llmBatchApi),
    scheduleOffWeekends: Boolean(o.scheduleOffWeekends),
  };
}

export function applySavingsToLambdaLine(
  lambdaRequestUsd: number,
  lambdaComputeUsd: number,
  savings: SavingsLevers,
): number {
  const compute = savings.spotOrPreemptibleCompute
    ? lambdaComputeUsd * SPOT_COMPUTE_FRACTION
    : lambdaComputeUsd;
  return lambdaRequestUsd + compute;
}

export function applySavingsDynamo(dynamoUsd: number, savings: SavingsLevers): number {
  return savings.dynamoCommittedCapacity
    ? dynamoUsd * DYNAMO_COMMITTED_FACTOR
    : dynamoUsd;
}

export function applySavingsLlm(llmUsd: number, savings: SavingsLevers): number {
  let x = savings.llmEconomyTier ? llmUsd * LLM_ECONOMY_FACTOR : llmUsd;
  if (savings.llmBatchApi) x *= LLM_BATCH_API_FACTOR;
  return x;
}
